import type { UserSchema, SimpleUserSchema } from '@gitbeaker/core'
import { createContextFunction, useEvent } from '@nartex/react-libs'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, type PropsWithChildren } from 'react'

import { useHttpClient } from './HTTPClient'

type SimpleIdentity = Pick<SimpleUserSchema, 'id' | 'name'>

type IdentityValue = (SimpleIdentity & { impersonated?: boolean }) | undefined
type IdentitySetter = (user: SimpleIdentity | null) => void

const [PrivateIdentityProvider, useIdentity] =
  createContextFunction<readonly [IdentityValue, IdentitySetter]>('Identity')

export { useIdentity }

export function IdentityProvider(props: PropsWithChildren<object>) {
  const { children } = props

  const me = useRealIdentity()

  const [impersonated, setImpersonated] = useState<SimpleIdentity | null>(null)

  const impersonate = useEvent<IdentitySetter>((user) => {
    if (!user) return setImpersonated(null)
    if (user.id === me?.id) return setImpersonated(null)

    return setImpersonated(user)
  })

  const user = useMemo<IdentityValue>(() => {
    if (!me) return undefined
    if (!impersonated || impersonated?.id === me?.id) {
      return { ...me, impersonated: false }
    }
    return {
      ...impersonated,
      impersonated: true,
    }
  }, [impersonated, me])

  const value = useMemo(() => {
    return [user, impersonate] as const
  }, [impersonate, user])

  return (
    <PrivateIdentityProvider value={value}>{children}</PrivateIdentityProvider>
  )
}

export function useRealIdentity() {
  const httpClient = useHttpClient()
  const { data } = useQuery<UserSchema>({
    queryKey: ['/user', httpClient],
    queryFn: async () => {
      const { data: me } = await httpClient.get<UserSchema>('/user')
      return me
    },
  })

  return data
}
